import React, { useState, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';
import { db } from '../../lib/supabase';

const AssignmentSubmission = ({ item, isOpen, onClose, onSubmitted }) => {
  const { currentUser } = useLMS();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('text');
  
  const [formData, setFormData] = useState({
    textContent: '',
    linkUrl: '',
    files: [],          // NEW: array of File objects pending upload
    existingFiles: []    // NEW: already-uploaded file records from DB
  });

  // Reset ALL form state when the modal opens for a (possibly different) assignment
  useEffect(() => {
    if (isOpen && item?.id && currentUser?.id) {
      // Always reset to blank state first so no stale data leaks between assignments
      setSubmission(null);
      setFormData({ textContent: '', linkUrl: '', files: [], existingFiles: [] });
      setActiveTab('text');
      loadSubmission();
    }
  }, [isOpen, item?.id, currentUser?.id]);

  const loadSubmission = async () => {
    setLoading(true);
    try {
      const { data, error } = await db.getSubmission(currentUser.id, item.id);
      if (data && !error) {
        setSubmission(data);

        // Load any multi-file records from the new table
        let existingFiles = [];
        try {
          const { data: files } = await db.getSubmissionFiles(data.id);
          if (files && files.length > 0) {
            existingFiles = files;
          }
        } catch (_) {
          // Table may not exist yet if migration hasn't run
        }

        // Backward compat: if no multi-file records but legacy single file exists, show it
        if (existingFiles.length === 0 && data.file_url) {
          existingFiles = [{
            id: 'legacy',
            file_url: data.file_url,
            file_name: data.file_name,
            file_size: data.file_size,
            file_type: data.file_type,
            sort_order: 0
          }];
        }

        // Pre-fill form with existing data
        setFormData({
          textContent: data.text_content || '',
          linkUrl: data.link_url || '',
          files: [],
          existingFiles
        });
        // Set active tab based on submission type
        if (data.submission_type) {
          setActiveTab(data.submission_type);
        }
      }
    } catch (error) {
      console.error('Error loading submission:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Determine if submission is late based on due date
      const isLate = itemDueDate ? new Date() > itemDueDate : false;

      let submissionData = {
        studentId: currentUser.id,
        moduleItemId: item.id,
        submissionType: activeTab,
        status: 'submitted',
        isLate,
        // Explicitly null out all content fields; only the active tab populates its own
        textContent: null,
        fileUrl: null,
        fileName: null,
        fileSize: null,
        fileType: null,
        linkUrl: null
      };

      // Handle different submission types
      if (activeTab === 'text') {
        if (!formData.textContent.trim()) {
          alert('Please enter your response.');
          setSubmitting(false);
          return;
        }
        submissionData.textContent = formData.textContent;
      } else if (activeTab === 'link') {
        if (!formData.linkUrl.trim()) {
          alert('Please enter a URL.');
          setSubmitting(false);
          return;
        }
        submissionData.linkUrl = formData.linkUrl;
      } else if (activeTab === 'file') {
        const hasNewFiles = formData.files.length > 0;
        const hasExistingFiles = formData.existingFiles.length > 0;

        if (!hasNewFiles && !hasExistingFiles) {
          alert('Please select at least one file to upload.');
          setSubmitting(false);
          return;
        }

        // Upload new files if any
        let uploadedFiles = [];
        if (hasNewFiles) {
          const { data: uploadData, error: uploadError } = await db.uploadSubmissionFiles(
            currentUser.id,
            item.id,
            formData.files
          );
          
          if (uploadError) {
            alert('Failed to upload files: ' + uploadError.message);
            setSubmitting(false);
            return;
          }
          uploadedFiles = uploadData;
        }

        // Combine existing + newly uploaded for saving to submission_files table
        const allFiles = [
          ...formData.existingFiles.filter(f => f.id !== 'legacy').map((f, i) => ({
            url: f.file_url,
            fileName: f.file_name,
            fileSize: f.file_size,
            fileType: f.file_type,
            path: f.storage_path || null,
            sortOrder: i
          })),
          ...uploadedFiles.map((f, i) => ({
            ...f,
            sortOrder: formData.existingFiles.filter(ef => ef.id !== 'legacy').length + i
          }))
        ];

        // For backward compat, set the first file in the legacy scalar columns
        const firstFile = allFiles[0];
        if (firstFile) {
          submissionData.fileUrl = firstFile.url;
          submissionData.fileName = firstFile.fileName;
          submissionData.fileSize = firstFile.fileSize;
          submissionData.fileType = firstFile.fileType;
        }

        // We'll save multi-file records after the submission is created/updated
        submissionData._pendingFiles = allFiles;
      }

      // Create or update submission
      let result;
      if (submission) {
        result = await db.updateSubmission(submission.id, submissionData);
      } else {
        result = await db.createSubmission(submissionData);
      }

      if (result.error) {
        alert('Failed to submit: ' + result.error.message);
      } else {
        // Save multi-file records if this is a file submission
        if (activeTab === 'file' && submissionData._pendingFiles && result.data?.id) {
          try {
            await db.saveSubmissionFiles(result.data.id, submissionData._pendingFiles);
          } catch (fileErr) {
            console.warn('Could not save multi-file records (migration may not be applied yet):', fileErr);
          }
        }

        setSubmission(result.data);
        if (onSubmitted) onSubmitted(result.data);
        alert('Assignment submitted successfully!');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit assignment.');
    }
    
    setSubmitting(false);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    // Check individual file sizes (10MB limit each)
    const oversized = selectedFiles.find(f => f.size > 10 * 1024 * 1024);
    if (oversized) {
      alert(`"${oversized.name}" exceeds the 10MB limit. Please select a smaller file.`);
      return;
    }

    // Check total count (existing + new combined max 10)
    const totalCount = formData.existingFiles.length + formData.files.length + selectedFiles.length;
    if (totalCount > 20) {
      alert('You can upload a maximum of 20 files per submission.');
      return;
    }

    setFormData(prev => ({ ...prev, files: [...prev.files, ...selectedFiles] }));
    // Reset the input so the same file can be re-selected if removed and re-added
    e.target.value = '';
  };

  const removeNewFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const removeExistingFile = (fileId) => {
    setFormData(prev => ({
      ...prev,
      existingFiles: prev.existingFiles.filter(f => f.id !== fileId)
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Draft', color: 'var(--gray-500)' },
      submitted: { label: 'Submitted', color: 'var(--blue-primary)' },
      reviewed: { label: 'Reviewed', color: '#FFA726' },
      graded: { label: 'Graded', color: 'var(--success)' },
      returned: { label: 'Returned', color: 'var(--error)' }
    };
    return badges[status] || badges.draft;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '';
    const num = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
    if (isNaN(num)) return bytes; // already formatted string like "2.5 MB"
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(num) / Math.log(k));
    return parseFloat((num / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  const isGraded = submission?.status === 'graded' || submission?.status === 'reviewed';
  const itemDueDate = item?.dueDate ? new Date(item.dueDate) : null;
  const isPastDue = itemDueDate ? new Date() > itemDueDate : false;
  // Allow late submissions - only block editing if already graded
  const canEdit = !isGraded;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal assignment-submission-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>{item?.title}</h3>
            <p className="assignment-meta">
              {item?.points && <span>{item.points} points</span>}
              {item?.dueDay && <span> &bull; Due {item.dueDay}</span>}
              {itemDueDate && (
                <span className={isPastDue ? 'due-date-past' : 'due-date-active'}>
                  {' '}&bull; {isPastDue ? 'Past due (late submissions accepted)' : `Due ${itemDueDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
                </span>
              )}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          ) : (
            <>
              {/* Assignment Description */}
              {item?.description && (
                <div className="assignment-description">
                  <h4>Instructions</h4>
                  <p>{item.description}</p>
                </div>
              )}

              {/* Existing Submission Status */}
              {submission && (
                <div className="submission-status-card">
                  <div className="status-header">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusBadge(submission.status).color }}
                    >
                      {getStatusBadge(submission.status).label}
                    </span>
                    {submission.is_late && (
                      <span
                        className="status-badge late-badge"
                        style={{ backgroundColor: '#ef4444', color: '#fff', marginLeft: '6px' }}
                      >
                        Late
                      </span>
                    )}
                    <span className="submitted-date">
                      Submitted {formatDate(submission.submitted_at)}
                    </span>
                  </div>
                  
                  {/* Show grade if graded */}
                  {submission.grade !== null && (
                    <div className="grade-display">
                      <div className="grade-score">
                        <span className="grade-value">{submission.grade}</span>
                        <span className="grade-max">/100</span>
                      </div>
                      {submission.points_earned !== null && item?.points && (
                        <div className="points-earned">
                          {submission.points_earned}/{item.points} points
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Show feedback if provided */}
                  {submission.feedback && (
                    <div className="feedback-section">
                      <h4>Instructor Feedback</h4>
                      <p>{submission.feedback}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Past Due Notice - still allows submission */}
              {isPastDue && !isGraded && !submission && (
                <div className="past-due-banner" style={{ backgroundColor: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' }}>
                  This assignment is past due. You may still submit, but it will be marked as late.
                </div>
              )}

              {/* Submission Form */}
              {canEdit && (
                <form onSubmit={handleSubmit} className="submission-form">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0 }}>{submission ? 'Update Your Submission' : 'Submit Your Work'}</h4>
                    <button type="submit" className="btn-primary" disabled={submitting} style={{ whiteSpace: 'nowrap' }}>
                      {submitting ? 'Submitting...' : (submission ? 'Update Submission' : 'Submit')}
                    </button>
                  </div>
                  
                  {/* Submission Type Tabs */}
                  <div className="submission-tabs">
                    <button
                      type="button"
                      className={`tab-btn ${activeTab === 'text' ? 'active' : ''}`}
                      onClick={() => setActiveTab('text')}
                    >
                      📝 Text Response
                    </button>
                    <button
                      type="button"
                      className={`tab-btn ${activeTab === 'file' ? 'active' : ''}`}
                      onClick={() => setActiveTab('file')}
                    >
                      📎 File Upload
                    </button>
                    <button
                      type="button"
                      className={`tab-btn ${activeTab === 'link' ? 'active' : ''}`}
                      onClick={() => setActiveTab('link')}
                    >
                      🔗 Link
                    </button>
                  </div>

                  {/* Text Response */}
                  {activeTab === 'text' && (
                    <div className="form-group">
                      <label>Your Response</label>
                      <textarea
                        rows={8}
                        placeholder="Enter your response here..."
                        value={formData.textContent}
                        onChange={(e) => setFormData({ ...formData, textContent: e.target.value })}
                      />
                      <p className="help-text">{formData.textContent.length} characters</p>
                    </div>
                  )}

                  {/* File Upload - MULTI-FILE */}
                  {activeTab === 'file' && (
                    <div className="form-group">
                      <label>Upload Files</label>
                      <div className="file-upload-area">
                        <input
                          type="file"
                          id={`submission-file-${item?.id}`}
                          key={`file-input-${item?.id}`}
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx,.txt,.rtf,.png,.jpg,.jpeg,.heic"
                          multiple
                        />
                        <label htmlFor={`submission-file-${item?.id}`} className="file-upload-label">
                          <span className="upload-icon">📤</span>
                          <span className="upload-text">Click or drag to upload files</span>
                          <span className="upload-hint">PDF, Word, Text, or Images (max 10MB each, up to 20 files)</span>
                        </label>
                      </div>

                      {/* Already-uploaded files (from previous submission) */}
                      {formData.existingFiles.length > 0 && (
                        <div className="file-list">
                          <p className="file-list-label">Previously uploaded:</p>
                          {formData.existingFiles.map((file) => (
                            <div key={file.id} className="file-list-item">
                              <span className="file-icon">📄</span>
                              <span className="file-name">{file.file_name}</span>
                              {file.file_size && <span className="file-size">{file.file_size}</span>}
                              <a 
                                href={file.file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="view-link"
                              >
                                View
                              </a>
                              <button
                                type="button"
                                className="file-remove-btn"
                                onClick={() => removeExistingFile(file.id)}
                                title="Remove file"
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Newly selected files (pending upload) */}
                      {formData.files.length > 0 && (
                        <div className="file-list">
                          <p className="file-list-label">New files to upload:</p>
                          {formData.files.map((file, index) => (
                            <div key={`new-${index}`} className="file-list-item">
                              <span className="file-icon">📎</span>
                              <span className="file-name">{file.name}</span>
                              <span className="file-size">{formatBytes(file.size)}</span>
                              <button
                                type="button"
                                className="file-remove-btn"
                                onClick={() => removeNewFile(index)}
                                title="Remove file"
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Total file count */}
                      {(formData.existingFiles.length + formData.files.length) > 0 && (
                        <p className="help-text">
                          {formData.existingFiles.length + formData.files.length} file{formData.existingFiles.length + formData.files.length !== 1 ? 's' : ''} total
                        </p>
                      )}
                    </div>
                  )}

                  {/* Link */}
                  {activeTab === 'link' && (
                    <div className="form-group">
                      <label>External Link</label>
                      <input
                        type="url"
                        placeholder="https://..."
                        value={formData.linkUrl}
                        onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                      />
                      <p className="help-text">Enter a link to your work (Google Doc, Dropbox, etc.)</p>
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={onClose}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={submitting}>
                      {submitting ? 'Submitting...' : (submission ? 'Update Submission' : 'Submit')}
                    </button>
                  </div>
                </form>
              )}

              {/* View-only for graded submissions */}
              {!canEdit && submission && (
                <div className="submission-view">
                  <h4>Your Submission</h4>
                  {submission.submission_type === 'text' && (
                    <div className="submitted-content text">
                      <p>{submission.text_content}</p>
                    </div>
                  )}
                  {submission.submission_type === 'file' && (
                    <div className="submitted-content file">
                      {formData.existingFiles.length > 0 ? (
                        <div className="file-list">
                          {formData.existingFiles.map((file) => (
                            <div key={file.id} className="file-list-item">
                              <span className="file-icon">📄</span>
                              <span className="file-name">{file.file_name}</span>
                              {file.file_size && <span className="file-size">{file.file_size}</span>}
                              <a 
                                href={file.file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn-secondary small"
                              >
                                Download
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <span className="file-icon">📄</span>
                          <span className="file-name">{submission.file_name}</span>
                          <a 
                            href={submission.file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn-secondary small"
                          >
                            Download
                          </a>
                        </>
                      )}
                    </div>
                  )}
                  {submission.submission_type === 'link' && (
                    <div className="submitted-content link">
                      <a 
                        href={submission.link_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {submission.link_url}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentSubmission;
